/**
 * Brightcode video perf — poster-first, deferred src, viewport play/pause.
 * Decorative videos stay muted/looping but do not buffer HD until in view.
 */
(function () {
  if (window.__bcVideoPerfInit) return;
  window.__bcVideoPerfInit = true;

  var SELECTOR = [
    'video[data-bc-viewport-video]',
    'video[data-card-responsive-video]',
    'video[data-slide-responsive-video]',
    'video-background-component video',
    '.hero__media video',
    'video.hero__media',
    'video.slide__video',
  ].join(',');

  var SLIDE_MQ = '(max-width: 749px)';
  var ATTACH_DELAY_MS = 900;
  var ATTACH_DELAY_MOBILE_SLIDE_MS = 4000;

  var reduceMotion = function () {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  };

  var saveData = function () {
    try {
      var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!c) return false;
      if (c.saveData) return true;
      var t = String(c.effectiveType || '');
      return t === 'slow-2g' || t === '2g';
    } catch (e) {
      return false;
    }
  };

  var inMagicCarousel = function (video) {
    return !!video.closest('[data-magic-carousel]');
  };

  var isActiveSlide = function (video) {
    var slide = video.closest('slideshow-slide');
    if (!slide) return true;
    if (slide.getAttribute('aria-hidden') === 'true') return false;
    var ss = slide.closest('slideshow-component');
    if (ss && typeof ss.current === 'number' && ss.slides) {
      for (var i = 0; i < ss.slides.length; i++) {
        if (ss.slides[i] === slide) return i === ss.current;
      }
    }
    return true;
  };

  var isRoughlyVisible = function (video) {
    var rect = video.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.height > 0 && rect.bottom > 40 && rect.top < vh - 40;
  };

  var pendingSrcOf = function (video) {
    if (video.dataset.pendingSrc) return video.dataset.pendingSrc;
    var desktop = video.dataset.desktopSrc || '';
    var mobile = video.dataset.mobileSrc || '';
    if (!desktop && !mobile) return video.dataset.bcVideoSrc || '';
    try {
      if (mobile && window.matchMedia(SLIDE_MQ).matches) return mobile;
    } catch (e) {
      /* ignore */
    }
    return desktop || mobile;
  };

  var detachSrc = function (video) {
    if (!(video instanceof HTMLVideoElement)) return;
    try {
      video.pause();
    } catch (e) {
      /* ignore */
    }
    if (video.getAttribute('src') || video.src) {
      video.removeAttribute('src');
      try {
        video.load();
      } catch (e2) {
        /* ignore */
      }
    }
    video.dataset.currentSrc = '';
    video.dataset.bcSrcAttached = '';
  };

  var attachSrc = function (video) {
    if (!(video instanceof HTMLVideoElement)) return false;
    var nextSrc = pendingSrcOf(video);
    if (!nextSrc) return !!(video.currentSrc || video.src);
    if (video.dataset.currentSrc === nextSrc && (video.getAttribute('src') || video.src)) {
      return true;
    }
    video.dataset.currentSrc = nextSrc;
    video.dataset.bcSrcAttached = '1';
    video.preload = 'none';
    video.setAttribute('preload', 'none');
    video.src = nextSrc;
    try {
      video.load();
    } catch (e) {
      /* ignore */
    }
    return true;
  };

  var prepare = function (video) {
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.dataset.bcVideoPrepared === '1') return;
    video.dataset.bcVideoPrepared = '1';

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.removeAttribute('autoplay');
    video.autoplay = false;

    if (!video.hasAttribute('loop')) video.setAttribute('loop', '');
    video.loop = true;

    // Defer network until attachSrc — never preload=auto on decorative clips
    video.preload = 'none';
    video.setAttribute('preload', 'none');

    // If markup already had a heavy src, park it as pending and detach
    var existing = video.getAttribute('src') || '';
    if (existing && (video.dataset.desktopSrc || video.dataset.mobileSrc || video.dataset.bcDeferSrc === '1')) {
      if (!video.dataset.pendingSrc) video.dataset.pendingSrc = existing;
      detachSrc(video);
    }
  };

  var safePlay = function (video) {
    if (reduceMotion()) return;
    if (saveData()) return;
    if (inMagicCarousel(video)) return;
    if (!isActiveSlide(video)) return;
    if (!attachSrc(video)) return;

    try {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
    } catch (e) {
      /* ignore */
    }
    var tryPlay = function () {
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    };
    tryPlay();
    if (video.readyState < 2) {
      video.addEventListener('loadeddata', tryPlay, { once: true });
      video.addEventListener('canplay', tryPlay, { once: true });
    }
  };

  var safePause = function (video) {
    if (inMagicCarousel(video)) return;
    if (!video.paused) video.pause();
  };

  var observed = new WeakSet();
  var attachTimers = new WeakMap();

  var clearAttachTimer = function (video) {
    var t = attachTimers.get(video);
    if (t) {
      clearTimeout(t);
      attachTimers.delete(video);
    }
  };

  var schedulePlay = function (video) {
    clearAttachTimer(video);
    if (reduceMotion() || saveData()) return;
    if (!isActiveSlide(video) || !isRoughlyVisible(video)) return;

    var delay = ATTACH_DELAY_MS;
    var isSlide = video.dataset.slideResponsiveVideo != null || video.classList.contains('slide__video');
    try {
      if (isSlide && window.matchMedia('(max-width: 749px)').matches) {
        delay = ATTACH_DELAY_MOBILE_SLIDE_MS;
      }
      var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (c && (c.effectiveType === '3g' || c.effectiveType === '2g')) {
        delay = Math.max(delay, isSlide ? 5000 : 2000);
      }
    } catch (e) {
      /* ignore */
    }

    var timer = setTimeout(function () {
      attachTimers.delete(video);
      if (!isActiveSlide(video) || !isRoughlyVisible(video)) return;
      safePlay(video);
    }, delay);
    attachTimers.set(video, timer);
  };

  var onIntersect = function (entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (!(video instanceof HTMLVideoElement)) return;
      if (inMagicCarousel(video)) return;

      if (entry.isIntersecting && isRoughlyVisible(video) && isActiveSlide(video)) {
        schedulePlay(video);
      } else {
        clearAttachTimer(video);
        safePause(video);
        // Drop buffer for off-screen / inactive slides so 4G is not wasted
        if (video.dataset.bcDeferSrc === '1' || video.dataset.slideResponsiveVideo != null) {
          detachSrc(video);
        }
      }
    });
  };

  var io = null;
  try {
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(onIntersect, {
        root: null,
        rootMargin: '20px 0px',
        threshold: [0, 0.01, 0.15, 0.35],
      });
    }
  } catch (e) {
    io = null;
  }

  var bind = function (video) {
    prepare(video);
    if (inMagicCarousel(video)) return;

    // Keep pending src from data attributes without attaching
    var pending = pendingSrcOf(video);
    if (pending) video.dataset.pendingSrc = pending;
    video.dataset.bcDeferSrc = video.dataset.bcDeferSrc || '1';
    if (!video.getAttribute('src')) {
      video.preload = 'none';
    }

    if (!io) {
      schedulePlay(video);
      return;
    }
    if (!observed.has(video)) {
      observed.add(video);
      io.observe(video);
    }
    if (isRoughlyVisible(video) && isActiveSlide(video)) schedulePlay(video);
    else {
      clearAttachTimer(video);
      safePause(video);
      detachSrc(video);
    }
  };

  var scan = function (root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTOR).forEach(bind);
    scope.querySelectorAll('.hero__media-wrapper video, .hero__content-wrapper video').forEach(bind);
  };

  var syncSlideVideos = function () {
    document.querySelectorAll('video[data-slide-responsive-video]').forEach(function (video) {
      var next = pendingSrcOf(video);
      if (next) video.dataset.pendingSrc = next;
      if (isActiveSlide(video) && isRoughlyVisible(video)) {
        // If already playing with old src, swap; else wait for schedule
        if (video.dataset.bcSrcAttached === '1' && video.dataset.currentSrc !== next) {
          detachSrc(video);
          schedulePlay(video);
        } else {
          schedulePlay(video);
        }
      } else {
        clearAttachTimer(video);
        safePause(video);
        detachSrc(video);
      }
    });
  };

  var ready = function () {
    scan(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target || document);
  });

  document.addEventListener('slideshow:select', function () {
    syncSlideVideos();
  });

  window.BcVideoPerf = {
    scan: scan,
    prepare: prepare,
    syncSlideVideos: syncSlideVideos,
    attachSrc: attachSrc,
    detachSrc: detachSrc,
    /** Call after updating data-desktop-src / data-mobile-src — do not attach yet */
    afterSrcChange: function (video) {
      prepare(video);
      var next = pendingSrcOf(video);
      if (next) video.dataset.pendingSrc = next;
      if (inMagicCarousel(video)) return;
      if (!io) {
        schedulePlay(video);
        return;
      }
      if (!observed.has(video)) {
        observed.add(video);
        io.observe(video);
      }
      if (isActiveSlide(video) && isRoughlyVisible(video)) schedulePlay(video);
      else {
        clearAttachTimer(video);
        safePause(video);
        detachSrc(video);
      }
    },
  };
})();
